import { push } from 'connected-react-router';
import { getCampaignsCurrentTabFromMatch, getCampaignsPageNumberFromUrl } from 'utils/campaign';
import { convertRegionsToMap } from 'utils/campaign/utils';
import { getPreparedAppliedCampaignsFilters } from 'utils/campaigns';
import { sortProfRoles } from 'utils/clickme';
import { CAMPAIGNS_DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';
import { PAGE_SIZE } from 'utils/constants/campaign';

import { urlParser } from '@hh.ru/browser-api-utils';

import { Requirements } from 'app/routes';

import { convertCampaignsToMap } from 'components/CampaignsDashboard/utils/utils';

import store from 'store';
import { PROF_ROLES, ProfRoles, RegionBE, setProfRoles } from 'store/autoReducers';
import { Action } from 'store/createReducer';
import {
    CampaignsCounts,
    CampaignsTab,
    DashboardCampaign,
    DEFAULT_PAGE_NUMBER,
    setCampaignRegions,
    setCampaigns,
    setCampaignsCounts,
    setCampaignsCurrentPage,
    setCampaignsCurrentTab,
    setCheckedCampaignIds,
    setIsCampaignsDetailsLoading,
    setIsCampaignsLoading,
    setIsCampaignsStatisticsLoading,
} from 'store/models/campaignsDashboard';

export const CampaignsDashboardRequirements = (): Requirements<{
    campaignCounts: CampaignsCounts;
    campaigns: DashboardCampaign[];
    regions: RegionBE[];
    profRoles: { professionalCategories: ProfRoles[] };
}> => {
    return {
        methods: ({ match, state }) => {
            const campaignsCurrentTab = getCampaignsCurrentTabFromMatch(match);
            const page = getCampaignsPageNumberFromUrl(match?.url);
            const appliedCampaignsFilters = state.campaignsFilters.appliedCampaignsFilters;
            const appliedCampaignsFiltersTotalCount = state.campaignsFilters.appliedCampaignsFiltersTotalCount;
            const campaignSearchValue = state.campaignsFilters.campaignsSearchValue;

            const filters = {
                nameFilter: campaignSearchValue,
                ...getPreparedAppliedCampaignsFilters(appliedCampaignsFilters, appliedCampaignsFiltersTotalCount),
            };

            const countQueryStr = urlParser.stringify({
                ...filters,
            });

            const campaignsQueryStr = urlParser.stringify({
                tab: campaignsCurrentTab,
                page,
                pageSize: PAGE_SIZE,
                ...filters,
            });

            return {
                campaignCounts: { url: `/api/v2/campaigns/count?${countQueryStr}`, cacheable: false },
                campaigns: {
                    url: `/api/v2/campaigns/?${campaignsQueryStr}`,
                    cacheable: false,
                },
                regions: { url: '/api/v1/catalog/areas' },
                [PROF_ROLES]: { url: '/api/v1/catalog/professionalCatalog' },
            };
        },
        resolve: ({ result: { campaignCounts, campaigns, regions, [PROF_ROLES]: profRoles }, match }) => {
            const campaignsCurrentTab = getCampaignsCurrentTabFromMatch(match);
            const pageNumber = getCampaignsPageNumberFromUrl(match?.url);
            const totalPages = Math.ceil(campaignCounts[campaignsCurrentTab] / PAGE_SIZE);

            if (pageNumber !== DEFAULT_PAGE_NUMBER && pageNumber > totalPages) {
                store.dispatch(
                    push(`${CAMPAIGNS_DASHBOARD_PAGE_ROUTE}${campaignsCurrentTab}?page=${Math.max(totalPages, 1)}`)
                );
                return [];
            }

            const campaignsMap = convertCampaignsToMap(campaigns);

            const pageActions: Action[] = [
                setCampaignsCounts({
                    [CampaignsTab.All]: campaignCounts.all || 0,
                    [CampaignsTab.Active]: campaignCounts.active || 0,
                    [CampaignsTab.Paused]: campaignCounts.paused || 0,
                    [CampaignsTab.Attention]: campaignCounts.attention || 0,
                    [CampaignsTab.Archived]: campaignCounts.archived || 0,
                }),
                setCampaignsCurrentTab(campaignsCurrentTab),
                setCampaignsCurrentPage(pageNumber),
                setCampaigns(campaignsMap),
                setCheckedCampaignIds(new Set()),
                setIsCampaignsLoading(false),
                setIsCampaignsDetailsLoading(true),
                setIsCampaignsStatisticsLoading(true),
                setCampaignRegions(convertRegionsToMap(regions)),
                ...(profRoles && [setProfRoles(sortProfRoles(profRoles.professionalCategories))]),
            ];

            return pageActions;
        },
    };
};
